import {BasicButton, TableActionButton} from "@goodgamestudios/asset-globals";
import {BasicModel, DictionaryUtil, TextFieldHelper} from "@goodgamestudios/core";
import {
    castAs,
    instanceOfClass,
    Keyboard,
    KeyboardEvent,
    TextFieldType,
    TextFormat
} from "@goodgamestudios/createjs-utilities";
import {int} from "@goodgamestudios/math-utilities";
import {Sine} from "gsap";
import {TweenMaxGroup} from "../helper/TweenMaxGroup";
import {SFConstants} from "../../constants/SFConstants";
import {Balancing} from "../../constants/shared/Balancing";
import {Constants} from "../../constants/shared/Constants";
import {Poker2BuddyEvent} from "../../event/Poker2BuddyEvent";
import {Poker2ChatEvent} from "../../event/Poker2ChatEvent";
import {Poker2OnTableEvent} from "../../event/Poker2OnTableEvent";
import {Poker2SliderEvent} from "../../event/Poker2SliderEvent";
import {Poker2TournamentDataEvent} from "../../event/Poker2TournamentDataEvent";
import {Poker2TutorialEvent} from "../../event/Poker2TutorialEvent";

import {SeatVO} from "../../gamevo/SeatVO";
import {Poker2Model} from "../../model/Poker2Model";
import {Poker2FadingDialogProperties} from "../dialogs/Poker2FadingDialogProperties";
import {CardHelper} from "../helper/CardHelper";
import {PokerTextHelper} from "../helper/PokerTextHelper";
import {Poker2Panel} from "./Poker2Panel";
import {CardRankingHelp} from "./tableAction/CardRankingHelp";
import {CardRankingHelp2} from "./tableAction/CardRankingHelp2";
import {getLogger} from "@goodgamestudios/logger";
import {Poker2TutorialController} from "../../controller/Poker2TutorialController";
import {Poker2FadingDialog} from "../dialogs/Poker2FadingDialog";
import {SliderHelperStepper} from "../helper/SliderHelperStepper";
import {TableChatHelper} from "../helper/TableChatHelper";
import {TableFriendHelper} from "../helper/TableFriendHelper";
import {TableLogHelper} from "../helper/TableLogHelper";
import {TableRankingHelper} from "../helper/TableRankingHelper";
import {Poker2LayoutManager} from "../Poker2LayoutManager";
import {TutorialHitAreaHelper} from "./tutorial/TutorialHitAreaHelper";
import DisplayObject = createjs.DisplayObject;
import MovieClip = createjs.MovieClip;
import MouseEvent = createjs.MouseEvent;
import Point = createjs.Point;
import Rectangle = createjs.Rectangle;

const logger = getLogger("Poker2TableActionPanel");

export class Poker2TableActionPanel extends Poker2Panel {
    public static NAME: string = "Poker2TableActionPanel";

    /** Identifies a FadingDialog to be able to hide this specific type of dialog (see LayoutManager)  **/
    private static TABLE_HINT_DIALOG: string = "table_hint_dialog";

    private BLOCK_STATE_INIT: number = 0;
    private BLOCK_STATE_HIDE: number = 1;
    private BLOCK_STATE_TOURNAMENT_END: number = 2;
    private BLOCK_STATE_ZOMBIE: number = 3;
    private BLOCK_STATE_WAITING: number = 4;
    private BLOCK_STATE_OPPONENT: number = 5;
    private BLOCK_STATE_TOURNAMENT_START: number = 6;

    private preSelectedButton: TableActionButton;
    private slider: SliderHelperStepper;

    private chatHelper: TableChatHelper;
    private logHelper: TableLogHelper;
    private rankingHelper: TableRankingHelper;
    private friendHelper: TableFriendHelper;

    private stateInit: boolean = false;
    private chatButtons: any[];

    private isWaiting: boolean = false;

    private CARD_HEIGHT: number = 80;
    private CARD_WIDTH: number = 70;
    private CARD_SPACE: number = 2;

    private STATE_ACTION: number = 0;
    private STATE_INIT: number = 1;
    private BIG_BLIND_RAISE_FACTOR: number = 3;
    private STEPS_FALLBACK: number = 10;
    private _stepSizeNumeric: number = NaN;
    private _betRange: number = NaN;
    private _cardRankingHelp: CardRankingHelp;
    private _cardRankingHelp2: CardRankingHelp2;

    constructor(disp: DisplayObject) {
        CONSTRUCTOR_HACK;
        super(disp);
    }

    protected init(): void {
        super.init();

        this.actionPanel.bg.uncache();
        this.actionPanel.bg.doCache(0, Poker2LayoutManager.GAME_SCALE_FACTOR);
        this.actionPanel.bg.x += 1;
        this.actionPanel.mc_help.bg.doCache(0, Poker2LayoutManager.GAME_SCALE_FACTOR);
        this.actionPanel.mc_help2.bg.doCache(0, Poker2LayoutManager.GAME_SCALE_FACTOR);

        this.chatButtons = [this.actionPanel.btn_chatfriend, this.actionPanel.btn_chatlog, this.actionPanel.btn_chatranking, this.actionPanel.btn_chattalk];

        this.slider = new SliderHelperStepper(this.actionPanel.mc_slider);
        this.chatHelper = new TableChatHelper(this.actionPanel.mc_chatview);
        this.logHelper = new TableLogHelper(this.actionPanel.mc_logview);
        this.rankingHelper = new TableRankingHelper(this.actionPanel.mc_rankingview);
        this.friendHelper = new TableFriendHelper(this.actionPanel.mc_friendview);

        this.actionPanel.btn_chatfriend.toolTipText = Poker2Model.languageData.getTextById("chat_btn_chatfriend");
        this.actionPanel.btn_chattalk.toolTipText = Poker2Model.languageData.getTextById("chat_btn_chattalk");
        this.actionPanel.btn_chatlog.toolTipText = Poker2Model.languageData.getTextById("chat_btn_chatlog");
        this.actionPanel.btn_chatranking.toolTipText = Poker2Model.languageData.getTextById("chat_btn_chatranking");
        this.actionPanel.mc_block.btn_sitdown.label = Poker2Model.languageData.getTextById("table_status_zombie_button");
        (this.actionPanel.mc_block.bg.bitmap9s_1 as any)._sliceCompensate = 0;

        // tooltip for player's cards
        this.actionPanel.cardTooltipLayer.toolTipText = Poker2Model.languageData.getTextById("table_ownCards");

        // create card ranking help
        this._cardRankingHelp = new CardRankingHelp(this.actionPanel.mc_help);

        // create card fold-btns help
        this._cardRankingHelp2 = new CardRankingHelp2(this.actionPanel.mc_help2);

        // init ranking help btn
        this.actionPanel.btn_help.tf_help.text = Poker2Model.languageData.getTextById("table_cardhelp_title");
        this.actionPanel.btn_help.mouseChildren = false;

        this.actionPanel.btn_help.icon_questionMark.doCache(0, Poker2LayoutManager.GAME_SCALE_FACTOR);

        // init fold-btn help btn
        this.actionPanel.btn_help2.label = Poker2Model.languageData.getTextById("menu_btn_help");

        // downscale cards a little bit due to the new ranking btn help
        this.actionPanel.mc_cardholder.scaleX = this.actionPanel.mc_cardholder.scaleY = 0.9;
        this.actionPanel.mc_cardholder.y = 527;

        // localize action button labels
        this.actionPanel.btn_allin.txt_label.defaultCacheScale = Poker2LayoutManager.GAME_SCALE_FACTOR;
        this.actionPanel.btn_raise.txt_label.defaultCacheScale = Poker2LayoutManager.GAME_SCALE_FACTOR;
        this.actionPanel.btn_call.txt_label.defaultCacheScale = Poker2LayoutManager.GAME_SCALE_FACTOR;
        this.actionPanel.btn_check.txt_label.defaultCacheScale = Poker2LayoutManager.GAME_SCALE_FACTOR;
        this.actionPanel.btn_fold.txt_label.defaultCacheScale = Poker2LayoutManager.GAME_SCALE_FACTOR;
        this.actionPanel.btn_checkfold.txt_label.defaultCacheScale = Poker2LayoutManager.GAME_SCALE_FACTOR;
        PokerTextHelper.writeLabel("table_actionbox_allin", this.actionPanel.btn_allin.txt_label, 9);
        PokerTextHelper.writeLabel("table_actionbox_raise", this.actionPanel.btn_raise.txt_label, 9);
        PokerTextHelper.writeLabel("table_actionbox_call", this.actionPanel.btn_call.txt_label, 9);
        PokerTextHelper.writeLabel("table_actionbox_check", this.actionPanel.btn_check.txt_label, 9);
        PokerTextHelper.writeLabel("table_actionbox_fold", this.actionPanel.btn_fold.txt_label, 9);
        TextFieldHelper.changeTextFromatSizeByTextWidth(9, this.actionPanel.btn_checkfold.txt_label, Poker2Model.languageData.getTextById("table_actionbox_check") + " / " + Poker2Model.languageData.getTextById("table_actionbox_fold"));
    }

    public show(): void {
        super.show();

        // make the control buttons have disabled checkmarks
        (this.actionPanel.btn_fold as TableActionButton).isSelected = false;
        (this.actionPanel.btn_call as TableActionButton).isSelected = false;
        (this.actionPanel.btn_check as TableActionButton).isSelected = false;
        (this.actionPanel.btn_checkfold as TableActionButton).isSelected = false;
        (this.actionPanel.btn_raise as TableActionButton).isSelected = false;
        (this.actionPanel.btn_allin as TableActionButton).isSelected = false;

        this.hideHelp();
        this.clearCardHolder();
        this.actionPanel.txt_handcategory.text = "";
        this.changeActionButtonState(this.STATE_INIT);
        this.activateTabButton(this.actionPanel.btn_chattalk);

        this.chatHelper.init();
        this.logHelper.init();
        this.rankingHelper.init();
        this.friendHelper.init();

        // correct btn_help2 position
        this.actionPanel.btn_help2.x = this.actionPanel.bg.x + this.actionPanel.bg.width - this.actionPanel.btn_help2.width * .5;

        //			chatHelper.enableSendMsg( Poker2Model.userData.isModerator );

        this.onSetBlockMC(this.BLOCK_STATE_INIT);
        this.actionPanel.txt_cash.text = (Poker2Model.onTableData.currentTable) ? String(Poker2Model.onTableData.currentTable.sBlind * 2) : "";

        this.controller.addEventListener("addmsg", this.chatHelper.bindFunction(this.chatHelper.onAddMsg));
        this.controller.addEventListener("addlog", this.logHelper.bindFunction(this.logHelper.onAddMsg));

        this.controller.addEventListener("changebuddydata", this.friendHelper.bindFunction(this.friendHelper.onUpdateFriends));

        this.controller.addEventListener("changeZombie", this.bindFunction(this.onChangeZombie));
        this.controller.addEventListener("tournamentEnd", this.bindFunction(this.onTournamentEnd));

        this.controller.addEventListener("playercards", this.bindFunction(this.onPlayerCards));
        this.controller.addEventListener("handcategory", this.bindFunction(this.onHandcategory));
        this.controller.addEventListener("startgame", this.bindFunction(this.onStartGame));
        this.controller.addEventListener("showdown", this.bindFunction(this.onFinishGame));
        this.controller.addEventListener("myplayerTurn", this.bindFunction(this.onPlayNow));
        this.controller.addEventListener("nextplayer", this.bindFunction(this.onNextPlayer));
        this.controller.addEventListener("sitDown", this.bindFunction(this.onSitDown));
        this.controller.addEventListener("standUp", this.bindFunction(this.onStandUp));
        this.controller.addEventListener("onEmptyTable", this.bindFunction(this.onEmptyTable));
        this.controller.addEventListener("unlockActionPanel", this.bindFunction(this.onUnlockActionButtons));
        this.controller.addEventListener("playerLeave", this.bindFunction(this.onPlayerLeave));
        this.controller.addEventListener("playerAction", this.bindFunction(this.onPlayerFold));
        this.controller.addEventListener("resetgame", this.bindFunction(this.onResetGame));
        this.controller.addEventListener("showdownAnimationComplete", this.bindFunction(this.onShowdownAnimationComplete));

        this.actionPanel.mc_slider.addEventListener("onSliding", this.bindFunction(this.onSliding));
        this.actionPanel.mc_slider.addEventListener("slideUp", this.bindFunction(this.onSliding));
        this.actionPanel.mc_slider.addEventListener("slideDown", this.bindFunction(this.onSliding));

        this.actionPanel.btn_help.addEventListener(MouseEvent.ROLL_OVER, this.bindFunction(this.showHelp));
        this.actionPanel.btn_help.addEventListener(MouseEvent.ROLL_OUT, this.bindFunction(this.hideHelp));

        this.actionPanel.btn_help2.addEventListener(MouseEvent.ROLL_OVER, this.bindFunction(this.showHelp2));
        this.actionPanel.btn_help2.addEventListener(MouseEvent.ROLL_OUT, this.bindFunction(this.hideHelp2));

        // if still open from last tutorial
        this._cardRankingHelp.fadeOut();
        this._cardRankingHelp2.fadeOut();
    }

    public hide(): void {
        this.controller.removeEventListener("addmsg", this.chatHelper.bindFunction(this.chatHelper.onAddMsg));
        this.controller.removeEventListener("addlog", this.logHelper.bindFunction(this.logHelper.onAddMsg));

        this.controller.removeEventListener("changebuddydata", this.friendHelper.bindFunction(this.friendHelper.onUpdateFriends));

        this.controller.removeEventListener("changeZombie", this.bindFunction(this.onChangeZombie));
        this.controller.removeEventListener("tournamentEnd", this.bindFunction(this.onTournamentEnd));

        this.controller.removeEventListener("playercards", this.bindFunction(this.onPlayerCards));
        this.controller.removeEventListener("handcategory", this.bindFunction(this.onHandcategory));
        this.controller.removeEventListener("startgame", this.bindFunction(this.onStartGame));
        this.controller.removeEventListener("showdown", this.bindFunction(this.onFinishGame));
        this.controller.removeEventListener("myplayerTurn", this.bindFunction(this.onPlayNow));
        this.controller.removeEventListener("nextplayer", this.bindFunction(this.onNextPlayer));
        this.controller.removeEventListener("sitDown", this.bindFunction(this.onSitDown));
        this.controller.removeEventListener("standUp", this.bindFunction(this.onStandUp));
        this.controller.removeEventListener("onEmptyTable", this.bindFunction(this.onEmptyTable));
        this.controller.removeEventListener("unlockActionPanel", this.bindFunction(this.onUnlockActionButtons));
        this.controller.removeEventListener("playerLeave", this.bindFunction(this.onPlayerLeave));
        this.controller.removeEventListener("playerAction", this.bindFunction(this.onPlayerFold));
        this.controller.removeEventListener("resetgame", this.bindFunction(this.onResetGame));
        this.controller.removeEventListener("showdownAnimationComplete", this.bindFunction(this.onShowdownAnimationComplete));

        this.actionPanel.mc_slider.removeEventListener("onSliding", this.bindFunction(this.onSliding));
        this.actionPanel.mc_slider.removeEventListener("slideUp", this.bindFunction(this.onSliding));
        this.actionPanel.mc_slider.removeEventListener("slideDown", this.bindFunction(this.onSliding));

        this.actionPanel.btn_help.removeEventListener(MouseEvent.ROLL_OVER, this.bindFunction(this.showHelp));
        this.actionPanel.btn_help.removeEventListener(MouseEvent.ROLL_OUT, this.bindFunction(this.hideHelp));
        super.hide();
    }

    public destroy(): void {
        this.chatHelper.destory();
        this.logHelper.destory();
        this.rankingHelper.destory();
        this.friendHelper.destory();

        if (this.slider)
            this.slider.destroy();
        this.slider = null;
        super.destroy();
    }

    private showHelp(event: MouseEvent): void {
        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step20_handStrength":
                    TutorialHitAreaHelper.removeTutorialHitArea();
                    this.tutorialController.nextStep();
                    return;
                    break;
                default:
                    return;
            }
        }
        this._cardRankingHelp.fadeIn();
    }

    private hideHelp(event: MouseEvent = null): void {
        if (this.tutorialController.isActive) {
            return;
        }
        this._cardRankingHelp.fadeOut();
    }

    private showHelp2(event: MouseEvent): void {
        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step11_actionHelp":
                    this.tutorialController.nextStep();
                    return;
                    break;
                default:
                    return;
            }
        }
        this._cardRankingHelp2.fadeIn();
    }

    private hideHelp2(event: MouseEvent = null): void {
        if (this.tutorialController.isActive) {
            return;
        }
        this._cardRankingHelp2.fadeOut();
    }

    private onTournamentEnd(event: Poker2TournamentDataEvent): void {
        this.clearCardHolder();
        this.onSetBlockMC(this.BLOCK_STATE_TOURNAMENT_END);
    }

    /**
     * <text id="table_status_tournamentover" name="Das Turnier ist vorbei!"  />
     <text id="table_status_waiting" name="Einen Augenblick! Die Runde läuft noch."  />
     <text id="table_status_waiting_forOpponent" name="Einen Augenblick! Es hat noch kein Gegner Platz genommen."  />
     <text id="table_status_zombie" name="Der Computer spielt das Turnier aktuell für Dich."  />
     */
    private onSetBlockMC(state: number): void {
        switch (state) {
            case this.BLOCK_STATE_INIT:
                this.actionPanel.mc_block.visible = true;
                this.actionPanel.mc_block.btn_sitdown.visible = false;
                this.actionPanel.mc_block.txt_copy.text = "";
                this.slider.enabled = false;
                break;
            case this.BLOCK_STATE_HIDE:
                this.actionPanel.mc_block.visible = false;
                this.slider.enabled = true;
                break;
            case this.BLOCK_STATE_TOURNAMENT_END:
                this.actionPanel.mc_block.visible = true;
                this.actionPanel.mc_block.btn_sitdown.visible = false;
                this.actionPanel.mc_block.txt_copy.text = Poker2Model.languageData.getTextById("table_status_tournamentover");
                break;
            case this.BLOCK_STATE_ZOMBIE:
                this.actionPanel.mc_block.visible = true;
                this.actionPanel.mc_block.btn_sitdown.visible = true;
                this.actionPanel.mc_block.txt_copy.text = Poker2Model.languageData.getTextById("table_status_zombie");
                break;
            case this.BLOCK_STATE_WAITING:
                this.actionPanel.mc_block.visible = true;
                this.actionPanel.mc_block.btn_sitdown.visible = false;
                this.actionPanel.mc_block.txt_copy.text = Poker2Model.languageData.getTextById("table_status_waiting");
                break;
            case this.BLOCK_STATE_OPPONENT:
                this.actionPanel.mc_block.visible = true;
                this.actionPanel.mc_block.btn_sitdown.visible = false;
                this.actionPanel.mc_block.txt_copy.text = Poker2Model.languageData.getTextById("table_status_waiting_forOpponent");
                break;
            case this.BLOCK_STATE_TOURNAMENT_START:
                this.actionPanel.mc_block.visible = true;
                this.actionPanel.mc_block.btn_sitdown.visible = false;
                this.actionPanel.mc_block.txt_copy.text = Poker2Model.languageData.getTextById("table_status_waiting_forStart");
                break;
        }

        // reset cash text formate
        if (this.actionPanel.txt_cash.defaultTextFormat != Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID) {
            this.actionPanel.txt_cash.defaultTextFormat = Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID;
            this.actionPanel.txt_cash.setTextFormat(Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID);
        }
    }

    private onPlayerLeave(event: Poker2OnTableEvent): void {
        var index: number = int(event.params[0]);

        if (index == Poker2Model.onTableData.myPlayerPos) {
            this.clearCardHolder();
            this.onSetBlockMC(this.BLOCK_STATE_INIT);
            this.resetCategory();
            // Reset slider to minimum raise, case:
            // Player done nothing and have been removed from the game. Now the take seat dialog is shown again,
            // but the cash textfield still shows the value the last raise of the last game. The slider will be resetted to
            // to the minimum raise.
            // disable slider
            this.slider.enabled = false;
            // zero percent is equal to the minimum raise
            this.slider.valueInPercent = 0;
            // reset cash textfield
            this.actionPanel.txt_cash.text = String(Poker2Model.onTableData.minimalRaiseCash);
        }
        this.friendHelper.onUpdateFriends();
        if (this.actionPanel.mc_rankingview.visible)
            this.rankingHelper.onUpdateRanking();
    }

    private onUnlockActionButtons(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.isWaiting = false;
        this.changeActionButtonState(this.STATE_ACTION, Poker2Model.onTableData.actionWindowStates);
    }

    /**
     * Zombie ersetzen oder als Zombie setzen
     */
    private onChangeZombie(event: Poker2TournamentDataEvent): void {
        var index: number = int(event.params[1]);

        if (index != Poker2Model.onTableData.myPlayerPos)
            return;

        var zombie: number = int(event.params[0]);

        if (zombie == 1) {
            this.clearCardHolder();
            this.onSetBlockMC(this.BLOCK_STATE_ZOMBIE);
        }
        else if (zombie == 0)
            this.onSetBlockMC((Poker2Model.onTableData.currentTable.tournamentInProgress) ? this.BLOCK_STATE_TOURNAMENT_START : this.BLOCK_STATE_HIDE);
    }

    /**
     * HINWEIS: darf nicht bei Tunieren kommen, da sollte changeZombie aufgerufen werden
     * Hingesetzt, entweder man muss noch warten bis Runde zu ende ist, bis sich leute an den Tisch gesetzt haben oder man kann gleich starten
     */
    private onSitDown(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();

        var fadingDialogText: string;

        if (Poker2Model.onTableData.isRunning) {
            // CASE: Game is running
            this.onSetBlockMC(this.BLOCK_STATE_WAITING);

            // show fading hint dialog: round is running, wait
            fadingDialogText = "table_status_waiting";
            if (!this.tutorialController.isActive)
                this.layoutManager.showDialog("Poker2FadingDialog", new Poker2FadingDialogProperties(Poker2Model.languageData.getTextById(fadingDialogText), 1.3, Poker2TableActionPanel.TABLE_HINT_DIALOG));
        }
        else if (!Poker2Model.onTableData.isRunning && this.playersSeated() > 1) {
            // CASE: Game is not running but there are more than player on the table

            // show fading hint dialog: wait until game starts
            fadingDialogText = "table_status_waiting_forStart";
            this.layoutManager.showDialog("Poker2FadingDialog", new Poker2FadingDialogProperties(Poker2Model.languageData.getTextById(fadingDialogText), 1.3, Poker2TableActionPanel.TABLE_HINT_DIALOG));
            return;
        }
        else if (Poker2Model.onTableData.playersSeatIndex.length == 0) {
            // CASE: Game is not running and one player is alone on the table

            this.onSetBlockMC(this.BLOCK_STATE_OPPONENT);

            // // show fading hint dialog: player is alone, wait for an opponent
            fadingDialogText = "table_status_waiting_forOpponent";
            this.layoutManager.showDialog("Poker2FadingDialog", new Poker2FadingDialogProperties(Poker2Model.languageData.getTextById(fadingDialogText), 1.3, Poker2TableActionPanel.TABLE_HINT_DIALOG));
        }
        else {
            // block the panel by default
            this.onSetBlockMC(this.BLOCK_STATE_INIT);
        }
    }

    // TODO: Is there an easier way to find out how many players ara currently sitting on table?
    private playersSeated(): number {
        var seats: any[] = Poker2Model.onTableData.currentTable.seats;
        var i: number = 0;
        var len: number = int(seats.length);
        var seatVO: SeatVO;
        var seatedCounter: number = 0;
        for (i = 0; i < len; ++i) {
            if (((seats[i]) as SeatVO).seated) {
                seatVO = seats[i];
                seatedCounter++;
            }
        }

        return seatedCounter;
    }

    /**
     * HINWEIS: darf nicht bei Tunieren kommen, da sollte changeZombie aufgerufen werden
     * Aufstehen und alles zurücksetzen
     */
    private onStandUp(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.resetCategory();
        this.clearCardHolder();
        this.onSetBlockMC(this.BLOCK_STATE_INIT);
    }

    private onPlayerCards(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.drawPlayerCards(true);
    }

    private onPlayerFold(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();

        var action: number = int(Poker2Model.onTableData.playerActions[0]);
        var index: number = int(Poker2Model.onTableData.playerActions[3]);
        var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(index);

        // beim Folden Karten weiterhin anzeigen, es sei denn man spielt als zombie
        if (action == 1 && index == Poker2Model.onTableData.myPlayerPos && seatVO.zombie == 0) {
            this.onSetBlockMC(this.BLOCK_STATE_HIDE);
        }
    }

    /**
     * Zeichnet die aktuellen Spielerkarten und löscht die alten
     */
    private drawPlayerCards(anim: boolean, highlightCards: any[] = null): void {
        this.clearCardHolder();

        if (Poker2Model.onTableData.playerCards == null)
            return;
        this.onSetBlockMC(this.BLOCK_STATE_HIDE);

        for (var i: number = 0; i < Poker2Model.onTableData.playerCards.length; i++) {
            var cardData: string = Poker2Model.onTableData.playerCards[i];
            var playerCard: MovieClip = CardHelper.drawCard(cardData, this.CARD_HEIGHT, this.CARD_WIDTH, highlightCards) as MovieClip;
            this.actionPanel.mc_cardholder.addChild(playerCard);
        }
        this.reposItemHolder(this.actionPanel.mc_cardholder, anim);

        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step9_holeCards":
                    // actionPanel.mc_cardholder.getRect(actionPanel.stage)
                    var rect: Rectangle = new Rectangle(303, 480, 160, 100);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
            }
        }
    }

    /**
     * Resets the card ranking popup view right after the end of the showdown animation.
     * @param event
     */
    protected onShowdownAnimationComplete(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.resetCategory();
    }

    private onHandcategory(event: Poker2OnTableEvent): void {
        var rankId: number = 0;
        if (event.params == null || event.params.length == 0 || event.params[0] < 0) {
            rankId = int(10);
        }
        else {
            rankId = int(event.params[0]);
        }

        this.updateHandCategory(rankId);
    }

    /**
     * Unselects the rank highlight of the card ranking help view.
     */
    private resetCategory(): void {
        this.updateHandCategory(10);
    }

    /**
     * Updates the handcategory text description (below the own card's view) as well as the highlight of the card rank
     * withing the ranking help popup.
     * @param rankId, int: 0-9 from highcard to royalFlush, 10 stands for no ranking selected
     */
    private updateHandCategory(rankId: number): void {
        if (rankId == 10) {
            this.actionPanel.txt_handcategory.text = "";

            // no card ranking highlighted
            this._cardRankingHelp.deselectRank();
        }
        else {
            // update card ranking help
            this._cardRankingHelp.updateRank(rankId);

            // show card ranking as text
            this.actionPanel.txt_handcategory.text = Poker2Model.languageData.getTextById("table_cardhelp_" + rankId);
        }
    }

    /**
     * Handkarten positionieren, mir oder ohne Animation
     */
    private reposItemHolder(itemholder: MovieClip, anim: boolean): void {
        for (var i: number = 0; i < itemholder.numChildren; i++) {
            var child: MovieClip = castAs<MovieClip>(itemholder.getChildAt(i), "MovieClip");

            if (!child)
                continue;

            child.x = (i * (this.CARD_WIDTH + this.CARD_SPACE)) - ((itemholder.numChildren - 1) * (this.CARD_WIDTH + this.CARD_SPACE)) / 2;

            if (anim) {
                var tween: TweenMaxGroup = new TweenMaxGroup();
                tween.fromTo(child, 0.25, {x: 150}, {
                    x: child.x,
                    ease: Sine.easeOut,
                    delay: i / 4,
                    onComplete: this.bindFunction(this.onCompleteCardAnim),
                    onCompleteParams: [tween]
                });
                tween.play();
            }
            else {
                child.y = 0;
            }
        }
    }

    private onCompleteCardAnim(tweenGroup: TweenMaxGroup): void {
        tweenGroup.destroy();
        tweenGroup = null;
    }

    private onSliding(event: Poker2SliderEvent): void {
        // ignore changes to binded input fields to slider
        if (this.tutorialController.isActive) {
            return;
        }
        if (event.onInputFieldChanged) {
            return;
        }

        var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(Poker2Model.onTableData.myPlayerPos);
        var min: number = Poker2Model.onTableData.minimalRaiseCash;
        var max: number = seatVO.handChips;
        this.actionPanel.txt_cash.text = String(Math.round(min + ((max - min) * event.percentValue / 100)));
    }

    /**
     * bei ENTER: CHAT aktivieren oder Nachricht senden
     * sonst raise-eingabefeld bestimmen
     */
    private static CASH_INPUTFIELD_FORMAT_VALID: TextFormat;
    private static CASH_INPUTFIELD_FORMAT_INVALID: TextFormat;

    protected onKeyUp(event: KeyboardEvent): void {
        if (event.key != Keyboard.ENTER) {
            var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(Poker2Model.onTableData.myPlayerPos);

            if (seatVO == null) {
                return;
            }

            var max: number = seatVO.handChips;
            var min: number = Poker2Model.onTableData.minimalRaiseCash;

            // inputted value lower than min, mark input text red to demonstrate that the input is invalid
            if (parseFloat(this.actionPanel.txt_cash.text) < min) {
                this.actionPanel.txt_cash.setTextFormat(Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_INVALID);
                return;
            }

            // inputted value higher than max, set to max
            if (parseFloat(this.actionPanel.txt_cash.text) > max) {
                this.actionPanel.txt_cash.text = max.toString();
                return;
            }

            if (this.actionPanel.txt_cash.defaultTextFormat != Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID) {
                this.actionPanel.txt_cash.setTextFormat(Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID);
            }
        }
        else {
            if (event.target == this.actionPanel.mc_chatview.txt_chatmsg)
                this.chatHelper.onSendMsg();
            else if (!this.actionPanel.mc_block.visible && this.actionPanel.mc_chatview.visible)
                this.actionPanel.stage.focus = this.actionPanel.mc_chatview.txt_chatmsg;
        }
    }

    /**
     * ActionButton de-/aktivieren
     */
    private changeActionButtonState(state: number, actions: Map<any, any> = null): void {
        var fo: boolean = actions ? DictionaryUtil.containsKey(actions, 1) : false;
        var ca: boolean = actions ? DictionaryUtil.containsKey(actions, 3) : false;
        var ch: boolean = actions ? DictionaryUtil.containsKey(actions, 2) : false;
        var cf: boolean = actions ? DictionaryUtil.containsKey(actions, 6) : false;
        var al: boolean = actions ? DictionaryUtil.containsKey(actions, 5) : false;
        var ra: boolean = actions ? DictionaryUtil.containsKey(actions, 4) : false;
        var s: boolean = state == this.STATE_ACTION;

        this.actionPanel.btn_fold.visible = s ? fo : true;
        this.actionPanel.btn_call.visible = s ? ca : true;
        this.actionPanel.btn_check.visible = s ? ch : true;
        this.actionPanel.btn_checkfold.visible = s ? cf : true;
        this.actionPanel.btn_raise.visible = s ? ra : false;
        this.actionPanel.btn_allin.visible = s ? al : false;
        // TODO: Slider will be hided if user goes ALL IN, but the slide will be still initialized with min==max, this is
        // invalid. Try finding a better solution
        this.setInput();
        this.actionPanel.btn_fold.activateButton = s ? fo : false;
        this.actionPanel.btn_call.activateButton = s ? ca : false;
        this.actionPanel.btn_check.activateButton = s ? ch : false;
        this.actionPanel.btn_checkfold.activateButton = s ? cf : false;
        this.actionPanel.btn_raise.activateButton = s ? ra : false;
        this.actionPanel.btn_allin.activateButton = s ? al : false;

        // HS1154
        this.actionPanel.btn_fold.toolTipText = fo ? null : Poker2Model.languageData.getTextById("table_actionbox_inactive_foldTooltip");
        this.actionPanel.btn_call.toolTipText = ca ? null : Poker2Model.languageData.getTextById("table_actionbox_inactive_callTooltip");
        this.actionPanel.btn_check.toolTipText = ch ? null : Poker2Model.languageData.getTextById("table_actionbox_inactive_checkTooltip");
        this.actionPanel.btn_checkfold.toolTipText = cf ? null : Poker2Model.languageData.getTextById("table_actionbox_inactive_checkFoldTooltip");
    }

    /**
     * Handles the player's own turn.
     * @param event
     */
    private onPlayNow(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();

        // close table hint dialogs, if visible
        this.hideTableHintDialogs();

        this.isWaiting = this.stateInit = false;
        this.onSetBlockMC(this.BLOCK_STATE_HIDE);
        this.actionPanel.txt_cash.text = String(Poker2Model.onTableData.minimalRaiseCash);

        if (this.preSelectedButton) {
            switch (this.preSelectedButton) {
                case this.actionPanel.btn_call:
                    this.gameAction(3);
                    break;
                case this.actionPanel.btn_check:
                    this.gameAction(2);
                    break;
                case this.actionPanel.btn_checkfold:
                    this.gameAction(6);
                    break;
                case this.actionPanel.btn_fold:
                    this.gameAction(1);
                    break;
            }
        }
        else {
            var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(Poker2Model.onTableData.myPlayerPos);
            this.changeActionButtonState(this.STATE_ACTION, Poker2Model.onTableData.actionWindowStates);

            if(seatVO) {
                // calculate bet range
                this._betRange = seatVO.handChips - Poker2Model.onTableData.minimalRaiseCash;

                // calculate numeric step size
                this._stepSizeNumeric = this.calculateNumericStepsize(Poker2Model.onTableData.minimalRaiseCash, seatVO.handChips);

                // initialize stepwise slider
                this.slider.initSliderStepper(Poker2Model.onTableData.minimalRaiseCash, seatVO.handChips, this._stepSizeNumeric, Poker2Model.onTableData.minimalRaiseCash, this.actionPanel.txt_cash);

                // enable slider
                this.slider.enabled = true;
            }
        }

        if (this.preSelectedButton)
            this.preSelectedButton.deselected();
        this.preSelectedButton = null;
    }

    private hideTableHintDialogs(): void {
        ((Poker2LayoutManager.getInstance()) as Poker2LayoutManager).fadeOutFadingDialogByType(Poker2TableActionPanel.TABLE_HINT_DIALOG, 0.3);
    }

    private calculateNumericStepsize(min: number, max: number): number {
        var smallBlind: number = NaN;
        var bigBlind: number = NaN;
        var stepSizeNumeric: number = NaN;

        if (Poker2Model.onTableData.currentTable.sBlind && Poker2Model.onTableData.currentTable.sBlind > 0) {
            smallBlind = Poker2Model.onTableData.currentTable.sBlind;

            bigBlind = smallBlind * 2;
            stepSizeNumeric = bigBlind * this.BIG_BLIND_RAISE_FACTOR;
        }
        else {
            // TODO: Check if 10 steps makes sense
            // Calculate static with 10 steps
            stepSizeNumeric = this._betRange / this.STEPS_FALLBACK;
            logger.warn("calculateNumericStepsize(): Poker2Model.onTableData.currentTable.sBlind not available. Calculate stepSizeNumeric with _betRange: " + this._betRange + " / STEPS_FALLBACK:  " + this.STEPS_FALLBACK + " = " + stepSizeNumeric);
        }

        return stepSizeNumeric;
    }

    private onNextPlayer(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.stateInit = true;
        this.changeActionButtonState(this.STATE_INIT);
    }

    private onStartGame(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();

        if (this.preSelectedButton)
            this.preSelectedButton.deselected();
        this.preSelectedButton = null;
        if (this.actionPanel.mc_rankingview.visible)
            this.rankingHelper.onUpdateRanking();
        this.friendHelper.onUpdateFriends();
    }

    private onFinishGame(event: Poker2OnTableEvent): void {
        this.isWaiting = true;
        var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(Poker2Model.onTableData.myPlayerPos);

        // block state nur ändern wenn kein zombie
        if (seatVO == null || seatVO.zombie == 0) {
            this.onSetBlockMC(this.BLOCK_STATE_INIT);
        }
        if (this.actionPanel.mc_rankingview.visible) {
            this.rankingHelper.onUpdateRanking();
        }
        this.friendHelper.onUpdateFriends();

        this.chatHelper.setFocusToChatInputField();
    }

    private onEmptyTable(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.clearCardHolder();
        this.onSetBlockMC(this.BLOCK_STATE_OPPONENT);
    }

    private clearCardHolder(): void {
        while (this.actionPanel.mc_cardholder.numChildren > 0) {
            this.actionPanel.mc_cardholder.removeChildAt(0);
        }
    }

    private onResetGame(event: Poker2OnTableEvent): void {
        this.chatHelper.setFocusToChatInputField();
        this.clearCardHolder();
        this.onSetBlockMC(this.BLOCK_STATE_OPPONENT);
    }

    protected onTutorialEvent(event: Poker2TutorialEvent): void {
        this.isWaiting = true;
        var rect: Rectangle;
        var p: Point = new Point();
        this.setInput();
        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step8_bigBlind":
                    this.clearCardHolder();
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step9_holeCards":
                    // actionPanel.mc_cardholder.getRect(layoutManager.tutPanel.tutorialPanel) // disp.stage
                    rect = new Rectangle(300, 570, 176, 23);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
                case "tut_step10_alinaFold":
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step11_actionHelp":
                    this._cardRankingHelp2.fadeOut();
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_help2);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step12_actionHelp2":
                    this._cardRankingHelp2.fadeIn();
                    rect = new Rectangle(580, 170, 200, 300);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
                case "tut_step13_playerActionCall":
                    this._cardRankingHelp2.fadeIn();
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_call, true, true);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step14_timoCall":
                    this._cardRankingHelp2.fadeOut();
                    break;
                case "tut_step16_pot":
                    rect = new Rectangle(355, 180, 95, 60);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
                case "tut_step17_flop":
                    rect = new Rectangle(285, 253, 140, 55);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
                case "tut_step18_timoCheck":
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step19_oliverCheck":
                    break;
                case "tut_step20_handStrength":
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_help);
                    rect = new Rectangle(300, 570, 176, 23);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    this._cardRankingHelp.fadeOut();
                    var hit = TutorialHitAreaHelper.addTutorialHitArea(new Rectangle(387, 584, 176, 23));
                    hit.addEventListener(MouseEvent.ROLL_OVER, this.bindFunction(this.showHelp));
                    hit.addEventListener(MouseEvent.ROLL_OUT, this.bindFunction(this.hideHelp));
                    break;
                case "tut_step21_handStrength2":
                    this._cardRankingHelp.fadeIn();
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step22_playerActionCheck":
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_check);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step23_turn":
                    this._cardRankingHelp.fadeOut();
                    rect = new Rectangle(425, 253, 50, 55);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
                case "tut_step24_timoRaise":
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step26_playerActionCall":
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_call, true, true);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step27_river":
                    rect = new Rectangle(470, 253, 50, 55);
                    this.layoutManager.tutPanel.showSpotlight(rect);
                    break;
                case "tut_step28_timoCheck":
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step29_playerActionRaise":
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_raise, true, true);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step30_timoReraise":
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step31_playerActionAllin":
                    this.layoutManager.tutPanel.showTutorialArrow(this.actionPanel.btn_allin);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
                case "tut_step32_timoAllin":
                    this.onSetBlockMC(this.BLOCK_STATE_HIDE);
                    break;
                case "tut_step34_victory":
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
            }
        }
    }

    protected onClick(event: MouseEvent): void {
        if (this.isLocked)
            return;

        if (this.tutorialController.isActive) {
            if (this.tutorialController.isActive) {
                switch (this.tutorialController.tutorialState) {
                    case "tut_step20_handStrength":
                        if (event.target == this.actionPanel.btn_help) {
                            this.tutorialController.nextStep();
                        }
                        break;
                    case "tut_step13_playerActionCall":
                        if (event.target == this.actionPanel.btn_call) {
                            this.tutorialController.nextStep();
                            this._cardRankingHelp2.fadeOut();
                        }
                        break;
                    case "tut_step22_playerActionCheck":
                        if (event.target == this.actionPanel.btn_check) {
                            this.tutorialController.nextStep();
                            this._cardRankingHelp.fadeOut();
                        }
                        break;
                    case "tut_step26_playerActionCall":
                        if (event.target == this.actionPanel.btn_call) {
                            this.tutorialController.nextStep();
                        }
                        break;
                    case "tut_step29_playerActionRaise":
                        if (event.target == this.actionPanel.btn_raise) {
                            this.tutorialController.nextStep();
                        }
                        break;
                    case "tut_step31_playerActionAllin":
                        if (event.target == this.actionPanel.btn_allin) {
                            this.tutorialController.nextStep();
                        }
                        break;
                    case "tut_step32_timoAllin":
                        if (event.target == this.actionPanel.btn_allin) {
                            this.tutorialController.nextStep();
                        }
                        break;
                }
            }
            return;
        }

        super.onClick(event);

        // Laschen der linken und mittleren Menüleiste
        switch (event.target) {
            case this.actionPanel.btn_chatfriend:
            case this.actionPanel.btn_chattalk:
            case this.actionPanel.btn_chatlog:
            case this.actionPanel.btn_chatranking:
                this.activateTabButton(event.target as BasicButton);
                break;
            case this.actionPanel.mc_block.btn_sitdown:

                if (instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "TournamentTypeAbstract")) {
                    BasicModel.smartfoxClient.sendMessage("jzo", [JSON.stringify({id: Poker2Model.tournamentData.selectedTournament})]);
                }
                else if (instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "SitngoTypeAbstract")) {
                    var paramObject: any = {};
                    BasicModel.smartfoxClient.sendMessage("szo", [JSON.stringify(paramObject)]);
                }

                //					BasicModel.smartfoxClient.sendMessage("jzo", [JSON.stringify( {id:Poker2Model.tournamentData.selectedTournament} )]);
                break;
        }

        // Buttons des rechten Menüs
        if (instanceOfClass(event.target, "TableActionButton") && (event.target as TableActionButton).isActive && !this.isWaiting)
            switch (event.target) {
                case this.actionPanel.btn_allin:
                    this.gameAction(5);
                    break;
                case this.actionPanel.btn_call:
                    this.gameAction(3);
                    break;
                case this.actionPanel.btn_check:
                    this.gameAction(2);
                    break;
                case this.actionPanel.btn_checkfold:
                    this.gameAction(6);
                    break;
                case this.actionPanel.btn_fold:
                    this.gameAction(1);
                    break;
                case this.actionPanel.btn_raise:
                    var raise: number = parseFloat(this.actionPanel.txt_cash.text);
                    this.gameAction(4, raise);
                    break;
            }

        // Vorausgewählte Actionbutton
        if (instanceOfClass(event.target, "TableActionButton") && !(event.target as TableActionButton).isActive) {
            (event.target as TableActionButton).toggleSelected();

            if (event.target.isSelected) {
                if (this.preSelectedButton && this.preSelectedButton.isSelected) {
                    this.preSelectedButton.toggleSelected();
                }
                this.preSelectedButton = (event.target as TableActionButton);
            }
            else
                this.preSelectedButton = null;
        }
    }

    private activateTabButton(selectedButton: BasicButton): void {
        for (var i: number = 0; i < this.chatButtons.length; i++)
            this.chatButtons[i].deselected();
        selectedButton.selected();

        this.actionPanel.mc_friendview.visible = (selectedButton == this.actionPanel.btn_chatfriend);
        this.actionPanel.mc_chatview.visible = (selectedButton == this.actionPanel.btn_chattalk);
        this.actionPanel.mc_logview.visible = (selectedButton == this.actionPanel.btn_chatlog);
        this.actionPanel.mc_rankingview.visible = (selectedButton == this.actionPanel.btn_chatranking);

        if (this.actionPanel.mc_rankingview.visible)
            this.rankingHelper.onUpdateRanking();
    }

    private gameAction(actionId: number, chips: number = 0): void {
        // if inputted raise value is lower than the mininum buyin, set the raise to the minimum buyin
        var min: number = int(Poker2Model.onTableData.minimalRaiseCash);
        if (chips < min) {
            chips = min;
            this.actionPanel.txt_cash.text = chips.toString();
        }

        // reset cash input field text formate
        this.actionPanel.txt_cash.defaultTextFormat = Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID;
        this.actionPanel.txt_cash.setTextFormat(Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID);

        if (this.stateInit) {
            return;
        }

        this.isWaiting = true;
        BasicModel.smartfoxClient.sendMessage("gac", [JSON.stringify({
            chips: chips,
            action: actionId
        })]);
    }

    // HS1267
    private setInput(): void {
        if (this.tutorialController.isActive) {
            // for tutorial cash-input and slider deactive
            this.actionPanel.txt_cash.type = TextFieldType.DYNAMIC;
            this.actionPanel.txt_cash.selectable = false;
            this.actionPanel.mc_slider.visible = false;
        }
        else {
            this.actionPanel.txt_cash.type = TextFieldType.INPUT;
            // if btn_raise is invisible the state is allin -> lock the input field due the user can't change it!
            this.actionPanel.txt_cash.selectable = (!this.actionPanel.btn_raise.visible) ? false : true;
            this.actionPanel.mc_slider.visible = this.actionPanel.btn_raise.visible;
        }
    }

    protected get actionPanel(): Library.PokerTableActionPanel.TableActionPanel {
        return this.disp as Library.PokerTableActionPanel.TableActionPanel;
    }

    public static __initialize_static_members() {
        //Poker2TableActionPanel.logger = Log.getLogger("Poker2TableActionPanel.as");
        Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_VALID = new TextFormat(null, null, 0x000000);
        Poker2TableActionPanel.CASH_INPUTFIELD_FORMAT_INVALID = new TextFormat(null, null, 0xB50101);
    }
}

Poker2TableActionPanel.__initialize_static_members();

